'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitType from 'split-type';
import styles from '../styles/AnimatedShowcase.module.css';
import Image from 'next/image';

gsap.registerPlugin(ScrollTrigger); // Register plugin outside component

const AnimatedShowcase = ({ 
  title = "Innovation Meets Sports Technology", 
  description = "Experience the future of pickleball with our cutting-edge platform. Our unique approach combines advanced technology with the fastest growing sport to create an unparalleled interactive experience.",
  tag = "GAM Technology",
  images = [],
}) => {
  const sectionRef = useRef(null);
  // Use a single root ref for gsap.context() for simplicity
  const mainRef = useRef(null);
  
  useEffect(() => {
    // Use gsap.context for scoping and automatic cleanup
    const ctx = gsap.context(() => {
      // Target elements within the context's scope
      const heading = mainRef.current.querySelector(`.${styles.heading} h2`);
      const description = mainRef.current.querySelector(`.${styles.description} p`);
      const tag = mainRef.current.querySelector(`.${styles.calloutTag}`);
      const imageGallery = mainRef.current.querySelector(`.${styles.imageGallery}`);
      
      if (!heading || !description || !tag || !imageGallery) return;
      
      // Split text for animation
      const splitHeading = new SplitType(heading, { types: 'chars, words' });
      const splitDesc = new SplitType(description, { types: 'chars, words' });
      
      // --- Main Timeline ---
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current, // Use sectionRef for the overall trigger
          start: 'top bottom-=100',
          end: 'bottom top',
          toggleActions: 'play none none reverse',
        }
      });
      
      // Animate the tag
      tl.fromTo(tag, 
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
      );
      
      // Animate the heading characters
      tl.fromTo(splitHeading.chars, 
        { y: 50, opacity: 0 },
        { 
          y: 0, 
          opacity: 1, 
          stagger: 0.02, 
          duration: 0.8, 
          ease: 'power3.out' 
        },
        '-=0.4'
      );
      
      // Animate the image gallery containers
      const images = imageGallery.querySelectorAll(`.${styles.imageContainer}`);
      tl.fromTo(images, 
        { y: 50, opacity: 0 },
        { 
          y: 0, 
          opacity: 1, 
          stagger: 0.15, 
          duration: 1, 
          ease: 'power2.out' 
        },
        '-=0.4' // Keep or adjust this timing based on visual flow
      );
      
      // --- Description Scrub Timeline (Separate) ---
      // Create a separate ScrollTrigger instance for the scrub animation
      gsap.fromTo(splitDesc.chars,
        { color: "rgba(255,255,255,0.6)" },
        {
          color: "#fff",
          stagger: 0.01,
          duration: 0.3,
          ease: "power1.inOut",
          scrollTrigger: {
            trigger: description, // Target the description paragraph directly
            start: "top 85%", // Adjust as needed
            end: "bottom 60%", // Adjust as needed
            scrub: true,
          }
        }
      );
      
    }, mainRef); // Scope the context to the main element
    
    // Cleanup function returned by gsap.context() is called automatically on unmount
    return () => ctx.revert();
  }, []); // Empty dependency array ensures this runs once on mount
  
  return (
    // Add mainRef to the section or a wrapping div if preferred
    <section className={styles.showcase} ref={sectionRef}>
      {/* Add mainRef here to scope the context */}
      <div className={styles.contentWrapper} ref={mainRef}>
        <div className={styles.textContent}>
          {/* Refs are no longer needed on individual elements */}
          <div className={styles.calloutTag}>
            <span>{tag}</span>
          </div>
          
          <div className={styles.heading}>
            <h2>{title}</h2>
          </div>
          
          <div className={styles.description}>
            {/* Removed descriptionRef */}
            <p>{description}<span style={{ opacity: 0 }}>.</span></p>
          </div>
        </div>
        
        <div className={styles.imageGallery}>
          {/* Removed imageGalleryRef */}
          {images.map((src, index) => (
            <div 
              key={index} 
              className={styles.imageContainer}
            >
              <Image 
                src={src} 
                alt={`Showcase ${index + 1}`} 
                fill={true}
                sizes="(max-width: 576px) 160px, (max-width: 768px) 200px, (max-width: 900px) 250px, 50vw"
                style={{ objectFit: 'contain' }}
                className={styles.showcaseImage}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AnimatedShowcase; 